/**
 * Website Make changes — the mode, mounted by WebsiteViewerPage
 * (docs/website_make_changes.md).
 *
 * The site keeps rendering through the real runtime; this hook adds what the
 * transaction mode adds to a screen: the violet bar, a ⚙ / ↑ ↓ control on each
 * section (through SmartPageRuntime's SectionChromeContext seam), a toolbar,
 * a ⚙ panel and typing on the page for each block (BlockChromeContext), the header
 * and footer ⚙, the panels, the Add a section / New page dialogs and a toast
 * with Undo. It loads and stores the page's OWN model (`/api/site-edit`); the
 * served copy is only used for what the server adds to it (embed tokens,
 * baked data — `overlayServed`).
 */

import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { Maximize2, Minimize2, Plus, Smartphone, Monitor, Palette, Sparkles } from 'lucide-react'
import {
  AskAiBox, CustomizeBar, GearButton, VIOLET, VIOLET_DARK, VIOLET_LINE, policyErrors,
} from '@twasta/page-runtime/customize'
import { AssetPickerModal } from '@/pages/designers/page/AssetPicker'
import { canEditObjectsFor } from '@/auth/useProjectPermissions'
import type { Block, PageModel, Section } from '@/pages/designers/page/types'
import type { SiteManifest } from '@/api/website'
import type { BlockChrome, SectionChrome } from '@/pages/workbench/smartpage-preview/sectionChrome'
import { addSitePage, getEditPage, getEditSite, patchSiteSettings, putEditPage, type SiteEditSite } from './api'
import { useSiteEditSession } from './session'
import {
  AI_IDEAS, AI_PLACEHOLDER, blockIndex, blockLabel, copyBlock, copySection, followSiteLook, ownLook, insertBlockAfter, insertSection,
  moveBlock, moveSection, overlayServed, removeBlock, removeSection, replaceBlockIn, replaceSection, sectionLabel, shownOn, typeInto,
  withSections, type TextTarget,
} from './pageOps'
import { PicturePickerContext } from './fields'
import { inHostFrame, postToHost } from './frameBridge'
import SectionPanel from './SectionPanel'
import BlockPanel from './BlockPanel'
import { BLOCK_CSS, EditableBlock } from './BlockChrome'
import { AddBlockDialog, AddSectionDialog, FooterPanel, HeaderPanel, NewPageDialog, StylePanel } from './SitePanels'

export interface SiteEditorHost {
  project: string
  manifest: SiteManifest | null
  /** obj_name of the page on screen. */
  activeObj: string | undefined
  /** `?make_changes=1` asked for the mode (never on a bound domain). */
  wantEdit: boolean
  goToPage: (obj: string) => void
  /** Re-read the site's manifest and header / footer (settings, menu, theme changed). */
  reloadSite: () => void
  /** Re-read the served page (on leaving the mode, after a theme change). */
  reloadPage: () => void
  onExit: () => void
}

type Panel =
  | { kind: 'section'; id: string; anchor: HTMLElement | null; draft: Section; original: Section }
  | { kind: 'block'; id: string; anchor: HTMLElement | null; draft: Block; original: Block }
  | { kind: 'header' | 'footer' | 'style'; anchor: HTMLElement | null }

export interface SiteEditor {
  active: boolean
  phone: boolean
  bar: ReactNode
  sectionChrome: SectionChrome | null
  blockChrome: BlockChrome | null
  /** The page to draw: the stored model (with the open panel's held changes). */
  displayPage: (served: PageModel | null) => PageModel | null
  /** Wrap the header / footer with its ⚙ while editing. */
  chromeControl: (tag: 'header' | 'footer', node: ReactNode) => ReactNode
  /** The phone frame around the site while previewing on a phone. */
  frame: (node: ReactNode) => ReactNode
  /** "+ Add a section" under the page's last section. */
  pageEnd: ReactNode
  overlays: ReactNode
}

const barBtn: React.CSSProperties = {
  height: 30, padding: '0 12px', borderRadius: 6, cursor: 'pointer', border: '1px solid #fff',
  background: 'rgba(255,255,255,.12)', color: '#fff', font: '600 13px inherit', fontFamily: 'inherit',
  display: 'inline-flex', alignItems: 'center', gap: 6,
}

export function useSiteEditor(h: SiteEditorHost): SiteEditor {
  const { project, manifest, activeObj } = h
  const siteId = manifest?.site_id || ''
  const [active, setActive] = useState(false)
  const [phone, setPhone] = useState(false)
  const session = useSiteEditSession(siteId)
  const { record, fail } = session
  // Inside the Workbench the mode opens FULL SCREEN over it, like a
  // transaction screen's Make changes; Restore puts it back in the pane.
  const hosted = useMemo(() => inHostFrame(), [])
  const [full, setFull] = useState(true)
  const [aiOpen, setAiOpen] = useState(false)
  const [pickPicture, setPickPicture] = useState<((url: string) => void) | null>(null)
  useEffect(() => {
    if (active && hosted) postToHost({ event: 'mode', on: true })
  }, [active, hosted])
  useEffect(() => {
    if (active && hosted) postToHost({ event: 'fullscreen', on: full })
  }, [active, hosted, full])

  // Builders only: the server refuses everyone else; this keeps the mode
  // from being offered to them at all.
  useEffect(() => {
    if (!h.wantEdit || !project) return
    let live = true
    canEditObjectsFor(project)
      .then((ok) => { if (live) setActive(ok) })
      .catch((e) => console.warn('[site-edit] access check failed', e))
    return () => { live = false }
  }, [h.wantEdit, project])

  // ── the site ──
  const [site, setSite] = useState<SiteEditSite | null>(null)
  const siteRef = useRef<SiteEditSite | null>(null)
  siteRef.current = site
  useEffect(() => {
    if (!active || !siteId) return
    let live = true
    getEditSite(project, siteId)
      .then((s) => { if (live) setSite(s) })
      .catch((e) => { if (live) fail(e) })
    return () => { live = false }
  }, [active, project, siteId, fail])

  // ── the page on screen ──
  const [stored, setStored] = useState<{ obj: string; page: PageModel } | null>(null)
  const storedRef = useRef(stored)
  storedRef.current = stored
  const activeRef = useRef(activeObj)
  activeRef.current = activeObj
  const hashes = useRef<Record<string, string>>({})
  const [pageRev, setPageRev] = useState(0)
  useEffect(() => {
    if (!active || !activeObj) { setStored(null); return }
    let live = true
    getEditPage(project, activeObj)
      .then((r) => {
        if (!live) return
        hashes.current[activeObj] = r.hash
        // A page of flat blocks is edited as one Main section, as the
        // designer does; the first stored change stores it that way.
        setStored({ obj: activeObj, page: withSections(r.page) })
      })
      .catch((e) => { if (live) fail(e) })
    return () => { live = false }
  }, [active, activeObj, project, pageRev, fail])

  // ── toast ──
  const [toast, setToast] = useState<{ text: string; undo: boolean } | null>(null)
  const toastTimer = useRef<number | undefined>(undefined)
  const flash = useCallback((text: string, undo = true) => {
    setToast({ text, undo })
    window.clearTimeout(toastTimer.current)
    toastTimer.current = window.setTimeout(() => setToast(null), 5000)
  }, [])
  useEffect(() => () => window.clearTimeout(toastTimer.current), [])

  // ── storing ──
  const putPage = useCallback(async (obj: string, page: PageModel) => {
    const r = await putEditPage(project, obj, page, hashes.current[obj] || '')
    hashes.current[obj] = r.hash
    // The viewer themes the page from its SERVED copy: a changed look
    // (page style, its colour bands) is read again from there.
    const prev = storedRef.current?.obj === obj ? storedRef.current.page : null
    const lookChanged = !prev || JSON.stringify([prev.theme, prev.designTokens]) !== JSON.stringify([r.page.theme, r.page.designTokens])
    if (activeRef.current === obj) {
      setStored({ obj, page: r.page })
      if (lookChanged && prev) h.reloadPage()
    } else h.goToPage(obj)   // an Undo of a change made on another page shows that page
  }, [project, h])
  const storePage = useCallback(async (label: string, next: PageModel) => {
    const cur = storedRef.current
    if (!cur) return false
    const obj = cur.obj
    const before = cur.page
    const ok = await record(label, () => putPage(obj, next), () => putPage(obj, before))
    if (ok) flash(label)
    return ok
  }, [record, putPage, flash])

  const applySettings = useCallback(async (set: Record<string, unknown>) => {
    const s = await patchSiteSettings(project, siteId, set, siteRef.current?.hash || '')
    siteRef.current = s
    setSite(s)
    h.reloadSite()
    postToHost({ event: 'site-changed' })
  }, [project, siteId, h])
  const saveSettings = useCallback(async (label: string, set: Record<string, unknown>, before: Record<string, unknown>) => {
    const ok = await record(label, () => applySettings(set), () => applySettings(before))
    if (ok) flash(label)
    return ok
  }, [record, applySettings, flash])

  const themeApplied = useCallback(() => {
    setPageRev((n) => n + 1)
    h.reloadSite()
    h.reloadPage()
    if (siteId) getEditSite(project, siteId).then(setSite).catch(fail)
  }, [h, project, siteId, fail])

  // ── panels & dialogs ──
  const [panel, setPanel] = useState<Panel | null>(null)
  const [adding, setAdding] = useState<number | null>(null)
  const [newPage, setNewPage] = useState(false)
  // "+ Add a block" after this block id.
  const [addingBlock, setAddingBlock] = useState<string | null>(null)
  useEffect(() => { setPanel(null); setAdding(null); setAddingBlock(null) }, [activeObj])

  const page = stored?.page || null
  const draftPage = useMemo(() => {
    if (!page) return null
    if (panel?.kind === 'section') return replaceSection(page, panel.draft)
    if (panel?.kind === 'block') return replaceBlockIn(page, panel.id, panel.draft)
    return page
  }, [page, panel])
  const blocks = useMemo(() => (draftPage ? blockIndex(draftPage) : new Map()), [draftPage])

  const openSection = (sec: Section, anchor: HTMLElement | null) => {
    const original = (page?.sections || []).find((s) => s.id === sec.id) || sec
    setPanel({ kind: 'section', id: sec.id, anchor, draft: original, original })
  }
  const sectionDone = async () => {
    if (panel?.kind !== 'section' || !page) { setPanel(null); return }
    const dirty = JSON.stringify(panel.draft) !== JSON.stringify(panel.original)
    const label = `Changed the ${sectionLabel(panel.draft).toLowerCase()} section`
    setPanel(null)
    if (dirty) await storePage(label, replaceSection(page, panel.draft))
  }

  const blockDone = async () => {
    if (panel?.kind !== 'block' || !page) { setPanel(null); return }
    const dirty = JSON.stringify(panel.draft) !== JSON.stringify(panel.original)
    const label = `Changed the ${blockLabel(panel.draft).toLowerCase()}`
    setPanel(null)
    if (dirty) await storePage(label, replaceBlockIn(page, panel.id, panel.draft))
  }
  // One change to one block, made on the page as it is stored now.
  const changeBlock = useCallback(async (label: string, make: (p: PageModel) => PageModel) => {
    const cur = storedRef.current?.page
    if (!cur) return false
    return storePage(label, make(cur))
  }, [storePage])

  const exit = useCallback(() => {
    setPanel(null)
    setAdding(null)
    setAddingBlock(null)
    setNewPage(false)
    setAiOpen(false)
    setActive(false)
    setFull(true)
    h.reloadPage()
    h.onExit()
    postToHost({ event: 'mode', on: false, changes: session.changeLog.length })
    if (session.changeLog.length) flash('Your changes are saved. Visitors see them after you publish.', false)
  }, [h, session.changeLog.length, flash])

  const sitePages = useMemo(() => (site?.pages || []).filter((p) => !p.chrome), [site])
  const designerHref = useCallback(
    (obj: string) => `/designers/page/${encodeURIComponent(project)}/${encodeURIComponent(obj)}`, [project])

  // ── section chrome (drawn around each section by the runtime) ──
  const sectionChrome: SectionChrome | null = useMemo(() => {
    if (!active || !page) return null
    const count = (draftPage?.sections || []).length
    return (sec, i, node) => {
      const selected = panel?.kind === 'section' && panel.id === sec.id
      const shown = shownOn(sec, phone ? 'mobile' : 'desktop')
      const name = sectionLabel(sec)
      return (
        <div className="swe-sec" data-tw-block={`section:${sec.id}`}
          style={{ position: 'relative', outline: selected ? `3px solid ${VIOLET}` : undefined, outlineOffset: -3 }}>
          {i === 0 && <AddLine label="+ Add a section here" onClick={() => setAdding(0)} />}
          <div className="swe-ctl" role="toolbar" aria-label={`${name} section`}>
            <span className="swe-ctl-name">{name}{shown ? '' : ' · hidden here'}</span>
            <SmallBtn label="Move this section up" disabled={i === 0}
              onClick={() => page && void storePage(`Moved the ${name.toLowerCase()} section up`, moveSection(page, i, -1))}>↑</SmallBtn>
            <SmallBtn label="Move this section down" disabled={i === count - 1}
              onClick={() => page && void storePage(`Moved the ${name.toLowerCase()} section down`, moveSection(page, i, 1))}>↓</SmallBtn>
            <GearButton label={`${name} section settings`} active={selected} onClick={(el) => openSection(sec, el)} />
          </div>
          {shown ? node : (
            <div style={{ padding: '18px 16px', textAlign: 'center', fontSize: 13, color: '#6b7280', background: '#f5f3ff', border: `1px dashed ${VIOLET_LINE}` }}>
              This section is not shown on {phone ? 'phones' : 'computers'}.
            </div>
          )}
          <AddLine label="+ Add a section here" onClick={() => setAdding(i + 1)} />
        </div>
      )
    }
  }, [active, page, draftPage, panel, phone, storePage]) // eslint-disable-line react-hooks/exhaustive-deps

  // ── block chrome (drawn around each block by the runtime) ──
  const blockChrome: BlockChrome | null = useMemo(() => {
    if (!active || !page) return null
    return (b, node) => {
      const spot = blocks.get(b.id)
      if (!spot) return node            // a repeated copy, a header / footer block
      const label = blockLabel(spot.block)
      const id = b.id
      return (
        <EditableBlock
          block={spot.block}
          label={label}
          selected={panel?.kind === 'block' && panel.id === id}
          canUp={spot.index > 0}
          canDown={spot.index < spot.count - 1}
          onOpen={(anchor) => {
            const original = blocks.get(id)?.block
            if (original) setPanel({ kind: 'block', id, anchor, draft: original, original })
          }}
          onTypingStart={() => setPanel(null)}
          onType={async (target: TextTarget, text: string) => {
            await changeBlock(`Changed the words of the ${label.toLowerCase()}`, (p) => {
              const cur = blockIndex(p).get(id)?.block
              return cur ? replaceBlockIn(p, id, typeInto(cur, target, text)) : p
            })
          }}
          onMove={(dir) => void changeBlock(`Moved the ${label.toLowerCase()} ${dir < 0 ? 'up' : 'down'}`, (p) => moveBlock(p, id, dir))}
          onCopy={() => void changeBlock(`Copied the ${label.toLowerCase()}`, (p) => copyBlock(p, id).page)}
          onDelete={() => { setPanel(null); void changeBlock(`Deleted the ${label.toLowerCase()}`, (p) => removeBlock(p, id)) }}
          onAdd={() => setAddingBlock(id)}
        >{node}</EditableBlock>
      )
    }
  }, [active, page, blocks, panel, changeBlock])

  const displayPage = useCallback((served: PageModel | null) => {
    if (!active || !draftPage || !stored || stored.obj !== activeObj) return served
    return overlayServed(served, draftPage)
  }, [active, draftPage, stored, activeObj])

  const chromeControl = useCallback((tag: 'header' | 'footer', node: ReactNode) => {
    if (!active) return node
    const label = tag === 'header' ? 'Header' : 'Footer'
    const on = panel?.kind === tag
    return (
      <div className="swe-sec" style={{ position: 'relative', outline: on ? `3px solid ${VIOLET}` : undefined, outlineOffset: -3 }}>
        {node}
        <div className="swe-ctl" role="toolbar" aria-label={label}>
          <span className="swe-ctl-name">{label}</span>
          <GearButton label={`${label} settings`} active={on} onClick={(el) => setPanel({ kind: tag, anchor: el })} />
        </div>
      </div>
    )
  }, [active, panel])

  const frame = useCallback((node: ReactNode) => {
    if (!active || !phone) return node
    return (
      <div style={{ background: '#e9edf2', padding: '20px 8px', minHeight: '100vh', boxSizing: 'border-box' }}>
        <div style={{ width: 390, maxWidth: '100%', margin: '0 auto', border: '10px solid #1c2430', borderRadius: 28, overflow: 'hidden', background: '#fff' }}>
          {node}
        </div>
      </div>
    )
  }, [active, phone])

  // ── the bar ──
  const pageOptions = (manifest?.pages || []).map((p) => ({ value: p.page_obj_name, label: p.nav_label || p.page_obj_name }))
  const siteLook = site?.settings.theme?.ref
  const siteName = site?.title || site?.node_title || manifest?.title || 'Website'
  const pageName = pageOptions.find((o) => o.value === activeObj)?.label || activeObj || ''
  // What "Ask AI to change" is pointed at: the website › the page › the section.
  const blockSection = panel?.kind === 'block'
    ? (draftPage?.sections || []).find((s) => s.id === blocks.get(panel.id)?.sectionId)
    : undefined
  const aiContext = [siteName, pageName && `the ${pageName} page`,
    panel?.kind === 'section' ? `the ${sectionLabel(panel.draft).toLowerCase()} section` : '',
    blockSection ? `the ${sectionLabel(blockSection).toLowerCase()} section` : '',
    panel?.kind === 'block' ? `the ${blockLabel(panel.draft).toLowerCase()} block` : '']
    .filter(Boolean).join(' › ')
  const bar = active ? (
    <div style={{ position: 'sticky', top: 0, zIndex: 9000 }}>
      <CustomizeBar
        title={siteName}
        subtitle="changes the website for everyone · visitors see it after you publish"
        session={session}
        scope="app"
        resetOptions={[]}
        onDone={exit}
        actions={hosted ? (
          <button type="button" style={barBtn} onClick={() => setFull((v) => !v)}
            data-tw-action="site-edit-fullscreen" aria-pressed={full}
            title={full ? 'Put the website back in its place in the Workbench' : 'Use the whole window'}>
            {full ? <><Minimize2 size={14} /> Restore</> : <><Maximize2 size={14} /> Full screen</>}
          </button>
        ) : undefined}
        toolsRow={(
          <>
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13.5, color: '#ddd6fe' }}>
              Page
              <select value={activeObj || ''} onChange={(e) => h.goToPage(e.target.value)} data-tw-field="site-edit-page"
                style={{ height: 30, borderRadius: 6, border: 0, padding: '0 8px', fontFamily: 'inherit', fontSize: 13.5, color: '#1c2430', minWidth: 140 }}>
                {pageOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </label>
            <button type="button" style={barBtn} onClick={() => setNewPage(true)} data-tw-action="site-edit-new-page" disabled={!site}>
              <Plus size={14} /> New page
            </button>
            <button type="button" style={barBtn} onClick={(e) => setPanel({ kind: 'style', anchor: e.currentTarget })}
              data-tw-action="site-edit-style" disabled={!site}>
              <Palette size={14} /> Website style
            </button>
            <span role="group" aria-label="Preview size" style={{ display: 'inline-flex', border: '1px solid rgba(255,255,255,.35)', borderRadius: 6, overflow: 'hidden' }}>
              {([['Computer', false, Monitor], ['Phone', true, Smartphone]] as const).map(([label, isPhone, Icon]) => (
                <button key={label} type="button" aria-pressed={phone === isPhone} onClick={() => setPhone(isPhone)}
                  data-tw-action={`site-edit-device:${label.toLowerCase()}`}
                  style={{
                    height: 28, padding: '0 10px', border: 0, cursor: 'pointer', fontFamily: 'inherit', fontSize: 13, fontWeight: 600,
                    display: 'inline-flex', alignItems: 'center', gap: 5,
                    background: phone === isPhone ? '#fff' : 'transparent', color: phone === isPhone ? VIOLET_DARK : '#fff',
                  }}><Icon size={13} />{label}</button>
              ))}
            </span>
            {hosted && (
              <button type="button" onClick={() => setAiOpen((v) => !v)} aria-expanded={aiOpen}
                data-tw-action="site-edit-ask-ai"
                style={{ ...barBtn, background: '#fff', color: VIOLET_DARK, borderColor: '#fff', fontWeight: 700 }}>
                <Sparkles size={14} /> Ask AI to change
              </button>
            )}
            {hosted && aiOpen && (
              <AskAiBox context={aiContext} ideas={AI_IDEAS} placeholder={AI_PLACEHOLDER}
                onClose={() => setAiOpen(false)}
                onSend={(text) => {
                  postToHost({ event: 'ask-ai', text: `${aiContext}: ${text}` })
                  // The Ask-AI panel is under the full-screen layer: step back
                  // into the pane so it can be seen and sent.
                  setFull(false)
                }} />
            )}
          </>
        )}
        note={!stored && activeObj ? 'Loading this page…' : undefined}
      />
      {page && siteLook && ownLook(page, siteLook) && (
        <div role="status" data-tw-screen="site-edit-own-look" style={{
          display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10, padding: '8px 16px',
          background: '#fffaeb', borderBottom: '1px solid #fedf89', color: '#7a2e0e', fontSize: 14,
          fontFamily: "'IBM Plex Sans', system-ui, sans-serif",
        }}>
          <span style={{ flex: '1 1 240px' }}>This page uses its own colours, not the website’s style.</span>
          <button type="button" data-tw-action="site-edit-follow-site-look"
            onClick={() => void storePage('This page uses the website’s style', followSiteLook(page, siteLook))}
            style={{ minHeight: 32, padding: '0 14px', borderRadius: 8, border: 0, background: VIOLET, color: '#fff', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>
            Use the website’s style
          </button>
        </div>
      )}
    </div>
  ) : null

  const sectionPanel = panel?.kind === 'section' && page ? (
    <SectionPanel
      anchor={panel.anchor}
      section={panel.draft}
      page={draftPage || page}
      pages={sitePages}
      dirty={JSON.stringify(panel.draft) !== JSON.stringify(panel.original)}
      onChange={(next) => setPanel((p) => (p && p.kind === 'section' ? { ...p, draft: next } : p))}
      onDone={() => void sectionDone()}
      onCancel={() => setPanel(null)}
      onDelete={() => {
        const name = sectionLabel(panel.original).toLowerCase()
        setPanel(null)
        void storePage(`Deleted the ${name} section`, removeSection(page, panel.id))
      }}
      onCopy={() => {
        const base = replaceSection(page, panel.draft)
        const { page: next } = copySection(base, panel.id)
        setPanel(null)
        void storePage(`Copied the ${sectionLabel(panel.draft).toLowerCase()} section`, next)
      }}
      designerHref={activeObj ? designerHref(activeObj) : '#'}
    />
  ) : null

  const blockPanel = panel?.kind === 'block' && page ? (
    <BlockPanel
      anchor={panel.anchor}
      block={panel.draft}
      sectionName={blockSection ? sectionLabel(blockSection) : 'page'}
      pages={sitePages}
      dirty={JSON.stringify(panel.draft) !== JSON.stringify(panel.original)}
      onChange={(next) => setPanel((p) => (p && p.kind === 'block' ? { ...p, draft: next } : p))}
      onDone={() => void blockDone()}
      onCancel={() => setPanel(null)}
      onDelete={() => {
        const id = panel.id
        const name = blockLabel(panel.original).toLowerCase()
        setPanel(null)
        void changeBlock(`Deleted the ${name}`, (p) => removeBlock(p, id))
      }}
      onCopy={() => {
        const { id, draft } = panel
        setPanel(null)
        void changeBlock(`Copied the ${blockLabel(draft).toLowerCase()}`, (p) => copyBlock(replaceBlockIn(p, id, draft), id).page)
      }}
      designerHref={activeObj ? designerHref(activeObj) : '#'}
    />
  ) : null

  // A function in state is stored through the updater form.
  const openPicturePicker = useCallback((onPick: (url: string) => void) => setPickPicture(() => onPick), [])

  const sections = page?.sections || []
  const addWhere = adding == null ? '' : adding >= sections.length
    ? 'It goes at the end of the page'
    : adding === 0 ? 'It goes at the top of the page' : `It goes after the ${sectionLabel(sections[adding - 1]).toLowerCase()} section`

  const overlays = active ? (
    <PicturePickerContext.Provider value={openPicturePicker}>
      <style>{EDIT_CSS + BLOCK_CSS}</style>
      {sectionPanel}
      {blockPanel}
      {addingBlock && page && (
        <AddBlockDialog
          where={(() => {
            const after = blocks.get(addingBlock)?.block
            return after ? `It goes after the ${blockLabel(after).toLowerCase()}` : 'It goes after this block'
          })()}
          onClose={() => setAddingBlock(null)}
          onAdd={(b) => {
            const after = addingBlock
            setAddingBlock(null)
            void changeBlock(`Added a ${blockLabel(b).toLowerCase()}`, (p) => insertBlockAfter(p, after, b))
          }} />
      )}
      {panel?.kind === 'header' && site && (
        <HeaderPanel anchor={panel.anchor} site={site} session={session} saveSettings={saveSettings}
          designerHref={designerHref} onClose={() => setPanel(null)} />
      )}
      {panel?.kind === 'footer' && site && (
        <FooterPanel anchor={panel.anchor} site={site} designerHref={designerHref} onClose={() => setPanel(null)} />
      )}
      {panel?.kind === 'style' && site && (
        <StylePanel anchor={panel.anchor} project={project} site={site} session={session}
          onApplied={themeApplied} onClose={() => setPanel(null)} />
      )}
      {adding != null && page && (
        <AddSectionDialog where={addWhere} onClose={() => setAdding(null)} onAdd={(sec) => {
          const at = adding
          setAdding(null)
          void storePage(`Added a ${sectionLabel(sec).toLowerCase()} section`, insertSection(page, at, sec))
        }} />
      )}
      {newPage && site && (
        <NewPageDialog onClose={() => setNewPage(false)} onAdd={async (body) => {
          try {
            const r = await addSitePage(project, site.site_id, body)
            siteRef.current = r.site
            setSite(r.site)
            setNewPage(false)
            h.reloadSite()
            postToHost({ event: 'site-changed' })
            h.goToPage(r.obj_name)
            flash(`Added the page “${body.title}”`, false)
            return null
          } catch (e) {
            console.warn('[site-edit] page could not be added', e)
            return policyErrors(e).join(' · ')
          }
        }} />
      )}
      {toast && (
        <div role="status" data-tw-msg="success" style={{
          position: 'fixed', left: 16, bottom: 16, zIndex: 9480, display: 'flex', gap: 12, alignItems: 'center',
          background: '#1c2430', color: '#fff', borderRadius: 10, padding: '10px 14px', fontSize: 14,
          boxShadow: '0 8px 24px rgba(0,0,0,.25)', fontFamily: "'IBM Plex Sans', system-ui, sans-serif",
        }}>
          <span>{toast.text}</span>
          {toast.undo && session.canUndo && (
            <button type="button" onClick={() => { setToast(null); void session.undo() }} data-tw-action="site-edit-toast-undo"
              style={{ background: 'transparent', border: 0, color: '#c4b5fd', fontWeight: 700, cursor: 'pointer', minHeight: 30, fontFamily: 'inherit' }}>
              Undo
            </button>
          )}
        </div>
      )}
      {pickPicture && (
        <AssetPickerModal project={project} accept="image/*" zIndex={9478}
          onClose={() => setPickPicture(null)}
          onSelect={(url) => { const done = pickPicture; setPickPicture(null); done(url) }} />
      )}
    </PicturePickerContext.Provider>
  ) : (toast ? (
    <div role="status" style={{
      position: 'fixed', left: 16, bottom: 16, zIndex: 9480, background: '#1c2430', color: '#fff',
      borderRadius: 10, padding: '10px 14px', fontSize: 14, fontFamily: "'IBM Plex Sans', system-ui, sans-serif",
    }}>{toast.text}</div>
  ) : null)

  const pageEnd = active && page ? (
    <div style={{ display: 'flex', justifyContent: 'center', padding: 16, background: '#faf8ff', borderTop: `1px dashed ${VIOLET_LINE}`, borderBottom: `1px dashed ${VIOLET_LINE}` }}>
      <button type="button" onClick={() => setAdding(sections.length)} data-tw-action="site-edit-add-section"
        style={{ minHeight: 40, padding: '0 18px', border: `1px dashed ${VIOLET_LINE}`, borderRadius: 8, background: '#fff', color: VIOLET_DARK, fontWeight: 600, cursor: 'pointer', fontFamily: "'IBM Plex Sans', system-ui, sans-serif", fontSize: 14 }}>
        + Add a section{sections.length === 0 ? ' — this page is empty' : ''}
      </button>
    </div>
  ) : null

  return { active, phone, bar, sectionChrome, blockChrome, displayPage, chromeControl, frame, pageEnd, overlays }
}

function SmallBtn({ label, onClick, disabled, children }: { label: string; onClick: () => void; disabled?: boolean; children: ReactNode }) {
  return (
    <button type="button" aria-label={label} title={label} disabled={disabled}
      onClick={(e) => { e.stopPropagation(); e.preventDefault(); onClick() }}
      data-tw-action={`site-edit-${label.toLowerCase().replace(/\W+/g, '-')}`}
      style={{
        width: 24, height: 24, borderRadius: 5, padding: 0, flexShrink: 0, fontSize: 13, lineHeight: 1,
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        border: '1px solid #ddd6fe', background: '#fff', color: VIOLET,
        cursor: disabled ? 'default' : 'pointer', opacity: disabled ? 0.4 : 1, fontFamily: 'inherit',
      }}>{children}</button>
  )
}

function AddLine({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <div className="swe-gap">
      <button type="button" onClick={onClick} data-tw-action="site-edit-add-section-here">{label}</button>
    </div>
  )
}

// The mode's own look — violet, IBM Plex, the transaction mode's values
// (customize/ui.tsx), scoped to `.swe-*` so it never reaches the site's CSS.
const EDIT_CSS = `
.swe-sec { min-height: 36px; }
.swe-sec:hover { box-shadow: inset 0 0 0 1px rgba(124,58,237,.35); }
.swe-ctl { position: absolute; top: 8px; right: 8px; z-index: 40; display: inline-flex; align-items: center; gap: 4px;
  padding: 3px 3px 3px 10px; border-radius: 8px; border: 1px solid #ddd6fe; background: #fff;
  box-shadow: 0 2px 8px rgba(76,29,149,.14); font: 600 13px 'IBM Plex Sans', system-ui, sans-serif; color: #4c1d95; }
.swe-ctl-name { padding-right: 4px; white-space: nowrap; }
.swe-gap { position: relative; height: 0; z-index: 41; display: flex; justify-content: center; }
.swe-gap button { position: absolute; top: -14px; min-height: 28px; padding: 0 12px; border-radius: 14px;
  border: 1px dashed #a78bfa; background: #fff; color: #4c1d95; cursor: pointer; opacity: 0;
  font: 600 13px 'IBM Plex Sans', system-ui, sans-serif; box-shadow: 0 2px 6px rgba(0,0,0,.1); transition: opacity .12s; }
.swe-sec:hover > .swe-gap button, .swe-gap button:focus-visible { opacity: 1; }
`
